import { useGsap } from "../hooks/useGsap";
import { gsap, ScrollTrigger, prefersReducedMotion } from "../lib/motion";

/**
 * Scroll-in reveals, driven by GSAP ScrollTrigger.
 *
 * These deliberately use the same engine as the pinned set-pieces. When reveals
 * ran on framer-motion's whileInView while GSAP owned the pins, the pin's layout
 * shift left whole sections stuck at opacity 0: two systems disagreeing about
 * where the page was. One engine, one source of truth.
 *
 * Initial states are set in a layout effect rather than in CSS, so a JS failure
 * leaves the content visible instead of blanking the page.
 */
export function Reveal({ children, className = "", stagger = 0.06, amount = 0.2 }) {
  const scope = useGsap((ctx, el) => {
    const found = el.querySelectorAll("[data-reveal-item]");
    const targets = found.length ? found : [el];
    if (prefersReducedMotion()) return;

    const reveal = () => gsap.fromTo(targets, { opacity: 0, y: 26, scale: 0.97 }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.6,
      ease: "power3.out",
      stagger,
    });

    // Do not set a hidden state until the trigger has successfully entered.
    // If ScrollTrigger is unavailable, the server-rendered/readable state wins.
    try {
      ScrollTrigger.create({
        trigger: el,
        /* Fires as the block enters the viewport, not once it is halfway up it.
           Anything later reads as lag rather than as motion. */
        start: `top ${Math.round(100 - amount * 10)}%`,
        once: true,
        onEnter: reveal,
      });
    } catch {
      // Motion is progressive enhancement; content must stay readable.
    }
  }, []);

  return (
    <div ref={scope} className={className}>
      {children}
    </div>
  );
}

export function Item({ children, className = "", as: Tag = "div", ...rest }) {
  return (
    <Tag data-reveal-item className={className} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * Headline lines rising from behind a hard mask edge.
 * trigger="mount" for above-the-fold copy; anything else waits for scroll.
 */
export function MaskLines({ lines, className = "", trigger = "inherit" }) {
  const scope = useGsap((ctx, el) => {
    const inner = el.querySelectorAll("[data-mask-line]");
    if (!inner.length) return;
    if (prefersReducedMotion()) return;

    const to = { yPercent: 0, duration: 0.52, ease: "power3.out", stagger: 0.06 };
    if (trigger === "mount") {
      gsap.fromTo(inner, { yPercent: 100 }, to);
    } else {
      // Applying the starting transform in onEnter makes this fail open if
      // ScrollTrigger cannot initialize during a route change or script error.
      try {
        ScrollTrigger.create({
          trigger: el,
          start: "top 94%",
          once: true,
          onEnter: () => gsap.fromTo(inner, { yPercent: 100 }, to),
        });
      } catch {
        // Motion is progressive enhancement; content must stay readable.
      }
    }
  }, []);

  return (
    <span ref={scope} className={className} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span key={i} className="reveal-mask" aria-hidden="true">
          <span data-mask-line style={{ display: "block" }}>
            {line}
          </span>
        </span>
      ))}
    </span>
  );
}
